// Centralized token storage.
//
// The app historically kept tokens in two localStorage formats:
//  - Microsoft/AD style: `access_token` / `refresh_token` (plain strings)
//  - Classic style: `accessToken` / `refreshToken` (JSON encoded)
// Reads preferred the MS keys while different refresh paths only updated one
// of the formats, so after refresh-token rotation AD sessions ended up reading
// a stale (blacklisted) token and failed to refresh. To keep every consumer in
// sync we always read from and write to BOTH formats through this module.

export const TOKEN_STORAGE_EVENT = 'r4r-tokens-updated'

export interface StoredTokens {
  access: string
  refresh: string
}

const parseMaybeJson = (value: string): string => {
  try {
    const parsed = JSON.parse(value)
    return typeof parsed === 'string' ? parsed : value
  } catch {
    return value
  }
}

export const getStoredAccessToken = (): string | null => {
  const msToken = localStorage.getItem('access_token')
  if (msToken && msToken !== 'null') {
    return parseMaybeJson(msToken)
  }
  const regularToken = localStorage.getItem('accessToken')
  if (regularToken && regularToken !== 'null') {
    return parseMaybeJson(regularToken)
  }
  return null
}

export const getStoredRefreshToken = (): string | null => {
  const msToken = localStorage.getItem('refresh_token')
  if (msToken && msToken !== 'null') {
    return parseMaybeJson(msToken)
  }
  const regularToken = localStorage.getItem('refreshToken')
  if (regularToken && regularToken !== 'null') {
    return parseMaybeJson(regularToken)
  }
  return null
}

// Persist tokens in every supported format so that all readers stay consistent
// regardless of which key they look at first. Emits an event so Jotai atoms can
// sync even when tokens are refreshed outside of React (e.g. axios interceptor).
export const storeTokens = ({ access, refresh }: StoredTokens): void => {
  localStorage.setItem('access_token', access)
  localStorage.setItem('refresh_token', refresh)
  localStorage.setItem('accessToken', JSON.stringify(access))
  localStorage.setItem('refreshToken', JSON.stringify(refresh))

  window.dispatchEvent(
    new CustomEvent<StoredTokens>(TOKEN_STORAGE_EVENT, { detail: { access, refresh } })
  )
}

export const clearStoredTokens = (): void => {
  localStorage.removeItem('accessToken')
  localStorage.removeItem('refreshToken')
  localStorage.removeItem('access_token')
  localStorage.removeItem('refresh_token')
  localStorage.removeItem('user_roles')
  localStorage.removeItem('user_data')
  localStorage.removeItem('user')
}

export const hasStoredTokens = (): boolean => !!getStoredAccessToken() && !!getStoredRefreshToken()
